import React from 'react';
import './JoinColumnDialog.css';

interface ColumnInfo {
  name: string;
  unique_count: number;
  null_count: number;
  uniqueness_ratio: number;
  is_suitable: boolean;
  sample_values: (string | number | boolean | null)[];
}

interface ColumnAnalysisData {
  columns: ColumnInfo[];
  suggested_join_column: string | null;
  total_rows: number;
}

interface JoinColumnDialogProps {
  open: boolean;
  columnAnalysis: ColumnAnalysisData | null;
  selectedColumn: string | null;
  onSelectColumn: (columnName: string) => void;
  onConfirm: () => void;
  onCancel: () => void;
}

export const JoinColumnDialog: React.FC<JoinColumnDialogProps> = ({
  open,
  columnAnalysis,
  selectedColumn,
  onSelectColumn,
  onConfirm,
  onCancel,
}) => {
  if (!open || !columnAnalysis) return null;

  const { columns, suggested_join_column, total_rows } = columnAnalysis;

  return (
    <div className="join-column-dialog-overlay" onClick={onCancel}>
      <div className="join-column-dialog-content" onClick={(e) => e.stopPropagation()}>
        <div className="join-column-dialog-header">
          <h2 className="join-column-dialog-title">Select Join Column for Data Merging</h2>
          <p className="join-column-dialog-description">
            Choose a column to use for joining the AI results with your original data.
            We've analyzed your file ({total_rows.toLocaleString()} rows) and suggested the best column below.
          </p>
        </div>

        <div className="join-column-dialog-body">
          {/* Info Box */}
          <div className="info-box">
            <div className="info-box-content">
              <svg className="info-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div>
                <p className="info-title">Why do we need a join column?</p>
                <p className="info-text">
                  The join column allows us to match AI-processed results back to your original data reliably,
                  even if processing fails partway through. Choose a column with unique or mostly unique values
                  (like Part Number, ID, SKU, etc.).
                </p>
              </div>
            </div>
          </div>

          {/* Suggested Column (if available) */}
          {suggested_join_column && (
            <div className="suggested-box">
              <div className="suggested-header">
                <svg className="suggested-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="suggested-label">Suggested Column</span>
              </div>
              <p className="suggested-text">
                We recommend using <strong>{suggested_join_column}</strong> as your join column
                based on its high uniqueness ratio.
              </p>
            </div>
          )}

          {/* Column List */}
          <div className="column-list-section">
            <h4 className="column-list-title">Available Columns:</h4>
            <div className="column-list">
              {columns.map((col) => {
                const isSelected = selectedColumn === col.name;
                const isSuggested = suggested_join_column === col.name;

                return (
                  <button
                    key={col.name}
                    onClick={() => onSelectColumn(col.name)}
                    className={`column-item ${isSelected ? 'selected' : ''} ${isSuggested ? 'suggested' : ''}`}
                  >
                    <div className="column-item-content">
                      <div className="column-header">
                        <span className="column-name">{col.name}</span>
                        {isSuggested && (
                          <span className="badge badge-suggested">Suggested</span>
                        )}
                        {isSelected && !isSuggested && (
                          <span className="badge badge-selected">Selected</span>
                        )}
                        {!col.is_suitable && (
                          <span className="badge badge-warning">Low Uniqueness</span>
                        )}
                      </div>

                      <div className="column-stats">
                        <div className="stat">
                          <span className="stat-label">Uniqueness:</span>{' '}
                          <span
                            className={`stat-value ${
                              col.uniqueness_ratio >= 0.8
                                ? 'stat-success'
                                : col.uniqueness_ratio >= 0.5
                                ? 'stat-warning'
                                : 'stat-danger'
                            }`}
                          >
                            {(col.uniqueness_ratio * 100).toFixed(1)}%
                          </span>
                        </div>
                        <div className="stat">
                          <span className="stat-label">Unique Values:</span>{' '}
                          <span className="stat-value">
                            {col.unique_count.toLocaleString()}
                          </span>
                        </div>
                        <div className="stat">
                          <span className="stat-label">Null Count:</span>{' '}
                          <span
                            className={`stat-value ${
                              col.null_count === 0 ? 'stat-success' : 'stat-warning'
                            }`}
                          >
                            {col.null_count}
                          </span>
                        </div>
                      </div>

                      {/* Sample Values */}
                      <div className="sample-values">
                        <span className="sample-label">Sample values:</span>{' '}
                        {col.sample_values.length > 0 ? (
                          <span className="sample-data">
                            {col.sample_values
                              .slice(0, 3)
                              .map((val) => (val === null ? 'null' : String(val)))
                              .join(', ')}
                            {col.sample_values.length > 3 && '...'}
                          </span>
                        ) : (
                          <span className="sample-empty">No samples available</span>
                        )}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="dialog-actions">
            <button className="btn btn-cancel" onClick={onCancel}>
              Cancel
            </button>
            <button
              className="btn btn-confirm"
              onClick={onConfirm}
              disabled={!selectedColumn}
            >
              Confirm & Start Job
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
